/**
 * Talker cancellation cutoff. Change CANCEL_FREE_NOTICE_HOURS (e.g. to 24)
 * to update Terms, FAQ, checkout, and the Cancel UI together.
 *
 * Flex: a free talker cancel uses `transition/customer-cancel` (refund +
 * cancel-booking). A late talker cancel reuses `transition/charge-after-session`
 * (capture + payout) — the same customer-actor path as a talker no-show.
 * No new process transition is required if Live already has
 * charge-after-session (default-booking / release-1 in this repo).
 */

import { bookingTimes } from './sessionClock';
import { isStartNowBooking } from './sessionPricing';

/** Hours before start when a talker cancel of a scheduled session stays free. */
export const CANCEL_FREE_NOTICE_HOURS = 1;

export const CANCEL_FREE_NOTICE_MS = CANCEL_FREE_NOTICE_HOURS * 60 * 60 * 1000;

export const LATE_CANCEL_CHARGE_TRANSITIONS = new Set([
  'transition/charge-after-session',
  'transition/provider-charge-after-session',
]);

/**
 * @returns {number}
 */
export const cancelNoticeHours = () => CANCEL_FREE_NOTICE_HOURS;

/**
 * User-facing window, e.g. "1 hour" or "24 hours".
 *
 * @returns {string}
 */
export const cancelNoticeLabel = () =>
  CANCEL_FREE_NOTICE_HOURS === 1 ? '1 hour' : `${CANCEL_FREE_NOTICE_HOURS} hours`;

const nowMsOf = now => {
  if (now instanceof Date) {
    return now.getTime();
  }
  const ms = typeof now === 'number' ? now : Date.parse(now);
  return Number.isFinite(ms) ? ms : Date.now();
};

/**
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const isStartNowTalkSession = tx => isStartNowBooking(null, tx);

/**
 * True when a talker cancel of this accepted scheduled session would charge
 * in full (inside the cutoff). Start now is never late. Listener cancels
 * never charge. A missing start is treated as free.
 *
 * @param {Object} [tx]
 * @param {Object} [opts]
 * @param {Date|number} [opts.now]
 * @param {string} [opts.role]
 * @returns {boolean}
 */
export const isTalkerLateCancel = (tx, { now = new Date(), role } = {}) => {
  if (role === 'provider') {
    return false;
  }
  if (isStartNowTalkSession(tx)) {
    return false;
  }
  const { start } = bookingTimes(tx);
  if (!start) {
    return false;
  }
  return start.getTime() - nowMsOf(now) < CANCEL_FREE_NOTICE_MS;
};

/**
 * After the fact: charged via charge-after-session before the booked start.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const isTalkerLateCancelCharge = tx => {
  if (!tx?.attributes || isStartNowTalkSession(tx)) {
    return false;
  }
  if (tx.attributes.protectedData?.lateCancel === true) {
    return true;
  }
  const { start } = bookingTimes(tx);
  if (!start) {
    return false;
  }
  const last = tx.attributes.lastTransition;
  const lastAt = Date.parse(tx.attributes.lastTransitionedAt);
  if (LATE_CANCEL_CHARGE_TRANSITIONS.has(last) && Number.isFinite(lastAt)) {
    return lastAt < start.getTime();
  }
  // Later transitions (review period expiry) must not turn it into "Completed".
  const history = Array.isArray(tx.attributes.transitions) ? tx.attributes.transitions : [];
  return history.some(t => {
    const at = t?.createdAt instanceof Date ? t.createdAt.getTime() : Date.parse(t?.createdAt);
    return (
      LATE_CANCEL_CHARGE_TRANSITIONS.has(t?.transition) &&
      t?.by === 'customer' &&
      Number.isFinite(at) &&
      at < start.getTime()
    );
  });
};

/**
 * Transition params for a talker cancel: a late cancel (charged in full) is
 * marked so emails and screens say "cancelled", not "session completed".
 *
 * @param {string} transitionName
 * @param {Date} [now]
 * @returns {Object}
 */
export const talkerCancelTransitionParams = (transitionName, now = new Date()) =>
  transitionName === 'transition/charge-after-session'
    ? { protectedData: { lateCancel: true, lateCancelAt: now.toISOString() } }
    : {};

/**
 * Copy id for the Cancel booking modal body.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.isCustomer]
 * @param {boolean} [opts.lateCancel]
 * @param {boolean} [opts.startNow]
 * @returns {string}
 */
export const cancelModalCopyId = ({
  isCustomer = true,
  lateCancel = false,
  startNow = false,
} = {}) => {
  if (!isCustomer) {
    return 'CancelBookingModal.listenerMoney';
  }
  if (startNow) {
    return 'CancelBookingModal.talkerStartNow';
  }
  if (lateCancel) {
    return 'CancelBookingModal.talkerLate';
  }
  return 'CancelBookingModal.talkerMoney';
};
